/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDButton from "components/MDButton";
// Data
import productTableData from "layouts/product/data/productTableData";
import { useNavigate } from "react-router-dom";
import { useMaterialUIController } from "context";
import CustomPagination from "examples/CustomPagination/CustomPagination";
import useProduct from "hooks/useProduct";
import { useEffect, useState } from "react";
import useDashboard from "hooks/useDashboard";
import { debounce } from "lodash";

function Tables() {
  const navigate = useNavigate();
  const { getProductList, productList } = useProduct();
  const { columns, rows } = productTableData();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const { searchTerm } = useDashboard();

  // Pagination states
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPage, setTotalPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Function to fetch dealers
  const fetchProduct = () => {
    const query = searchTerm.length >= 3 ? searchTerm : ""; // Apply search only if length >= 3
    getProductList({ limit, page, search: query });
  };

  // Debounced search function
  const debouncedfetchProduct = debounce(fetchProduct, 500);

  // Initial Load (Mandatory)
  useEffect(() => {
    fetchProduct(); // Always fetch initial data on mount
  }, []);

  // Search & Pagination Updates
  useEffect(() => {
    if (searchTerm.length === 0 || searchTerm.length >= 3) {
      debouncedfetchProduct();
    }
    return () => debouncedfetchProduct.cancel(); // Cleanup debounce
  }, [searchTerm, page, limit]);

  // Update pagination state when productList updates
  useEffect(() => {
    if (productList.page && productList.limit) {
      setPage(productList.page);
      setLimit(productList.limit);
      setTotalPage(productList.pages || 1);
      setTotal(productList.total || 0);
    }
  }, [productList]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setPage(newPage);
    getProductList({ limit: 10, page: newPage });
  };
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                bgColor={sidenavColor}
                borderRadius="lg"
                coloredShadow={sidenavColor}
              >
                <MDTypography variant="h6" color="white">
                  Product Table
                </MDTypography>
                <MDButton onClick={() => navigate("/product-management/create")}>
                  Create Product
                </MDButton>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
            <MDBox
              sx={{ width: "100%", display: "flex", justifyContent: "start", marginTop: "1rem" }}
            >
              <CustomPagination
                totalCount={totalPage}
                currentPage={page}
                onPageChange={handlePageChange}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default Tables;
